import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import ValidationAlert from '../../../app/components/ValidationAlert';
import Button from '../../../common/components/Button/Button';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { RecommendedPublisherScopeEnum } from '../../../common/pokCore/validation/enums';
import {
  CreateUpdateRecommendedPublisherDto,
  GetLastEditorDto,
  GetRecommendationsDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import FormSkeleton from '../../../app/components/FormSkeleton';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { LastEditorInfoDisplay } from '../../../app/components/LastEditorInfoDisplay';

import { RecommendedPublisherEditorForm } from './RecommendedPublisherEditorForm';

interface RecommendedPublisherModalEditorProps {
  profileId: string;
  profileName?: string;
  scopeType: RecommendedPublisherScopeEnum;
  companyId?: string;
  purchaserId?: string;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: () => void;
}

export const RecommendedPublisherModalEditor: React.FC<
  RecommendedPublisherModalEditorProps
> = props => {
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [lockedClientIds, setLockedClientIds] = useState<string[]>([]);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [lastEditor, setLastEditor] = useState<GetLastEditorDto>();
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (props.show && props.profileId) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setSelectedClientIds([]);
      setLockedClientIds([]);
      setDataLoading(true);

      pok.recommendedPublishers
        .getSelectedPublishers(
          props.profileId,
          props.companyId,
          props.purchaserId,
        )
        .then((recommendations: GetRecommendationsDto[]) => {
          const currentScopeIds: string[] = [];
          const inheritedIds: string[] = [];

          recommendations.forEach(rec => {
            if (rec.scopeType === props.scopeType) {
              currentScopeIds.push(rec.clientId);
            } else {
              inheritedIds.push(rec.clientId);
            }
          });

          setSelectedClientIds(currentScopeIds);
          setLockedClientIds(inheritedIds);
          setDataLoading(false);
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
          setDataLoading(false);
        });
    }
  }, [
    notifications,
    pok.recommendedPublishers,
    props.companyId,
    props.profileId,
    props.purchaserId,
    props.scopeType,
    props.show,
  ]);

  const handleSave = async () => {
    setIsLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);

    try {
      const dto: CreateUpdateRecommendedPublisherDto = {
        profileId: props.profileId,
        scopeType: props.scopeType,
        clientIds: selectedClientIds,
        companyId: props.companyId,
        purchaserId: props.purchaserId,
        active: true,
      };

      await pok.recommendedPublishers.saveRecommendations(dto);
      notifications.saveCompleted();
      props.onCompleted();
      if (props.handleClose) {
        props.handleClose();
      }
    } catch (errorResponse) {
      const errors = await responseErrors(errorResponse as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedClientIds(newSelectedIds);
  };

  const getLastEditor = () => {
    setIsLoadingInfo(true);
    setLastEditor(undefined);
    pok.recommendedPublishers
      .getLastEditor(props.profileId, props.companyId, props.purchaserId)
      .then(info => {
        setLastEditor(info);
        setIsLoadingInfo(false);
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
        setIsLoadingInfo(false);
      });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(tk.recommendedPublisher.editRecommendations)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {dataLoading ? (
          <FormSkeleton />
        ) : (
          <RecommendedPublisherEditorForm
            profileId={props.profileId}
            profileName={props.profileName}
            scopeType={props.scopeType}
            companyId={props.companyId}
            purchaserId={props.purchaserId}
            selectedClientIds={selectedClientIds}
            lockedClientIds={lockedClientIds}
            onSelectionChange={handleSelectionChange}
          />
        )}
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <LastEditorInfoDisplay
          lastEditor={lastEditor}
          isLoading={isLoadingInfo}
          onEnter={getLastEditor}
        />
        <Button
          variant="outline-secondary"
          onClick={props.handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          isLoading={isLoading}
          disabled={dataLoading}
        >
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
